/* Juicyfolks CRO v5 — Emporium 4.0.0
   Comportamiento de los módulos añadidos tras la auditoría CRO de agosto 2026.
   Todo es aditivo: si un módulo no existe en la página, su bloque simplemente no corre. */
(() => {
  'use strict';

  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        // Modo privado: el módulo sigue funcionando, sólo no recuerda el estado.
      }
    },
  };

  /* ---------------------------------------------------------------
     Validador de cobertura por código postal
     --------------------------------------------------------------- */
  function readZones(root) {
    const dataNode = root.querySelector('[data-jf-zip-data]');
    if (!dataNode) return [];

    try {
      return JSON.parse(dataNode.textContent) || [];
    } catch (error) {
      return [];
    }
  }

  function findZone(zones, postalCode) {
    const prefix = postalCode.slice(0, 2);
    return zones.find((zone) => Array.isArray(zone.prefixes) && zone.prefixes.includes(prefix)) || null;
  }

  function checkPostalCode(root) {
    const input = root.querySelector('[data-jf-zip-input]');
    const result = root.querySelector('[data-jf-zip-result]');
    if (!input || !result) return;

    const postalCode = (input.value || '').replace(/\D/g, '').slice(0, 5);
    input.value = postalCode;

    result.classList.remove('is-covered', 'is-unknown');

    if (postalCode.length < 5) {
      result.classList.add('is-visible', 'is-unknown');
      result.innerHTML = 'Escribe los 5 dígitos de tu código postal.';
      return;
    }

    const zone = findZone(readZones(root), postalCode);

    if (zone) {
      result.classList.add('is-visible', 'is-covered');
      result.innerHTML = `<strong>Sí llegamos a ${zone.name}.</strong> ${zone.eta}.`;
    } else {
      result.classList.add('is-visible', 'is-unknown');
      const whatsapp = root.dataset.jfWhatsapp;
      const link = whatsapp
        ? `<a class="jf-zip__wa" href="https://wa.me/${whatsapp}?text=${encodeURIComponent('Hola, quiero confirmar si llegan a mi CP ' + postalCode)}" target="_blank" rel="noopener">Confirmar por WhatsApp</a>`
        : '';
      result.innerHTML = `${root.dataset.jfZipFallback || ''} ${link}`;
    }

    storage.set('jf_zip', postalCode);
  }

  function initPostalCodeCheckers(scope) {
    scope.querySelectorAll('[data-jf-zip]').forEach((root) => {
      if (root.dataset.jfZipReady === 'true') return;
      root.dataset.jfZipReady = 'true';

      const saved = storage.get('jf_zip');
      const input = root.querySelector('[data-jf-zip-input]');

      if (saved && input && !input.value) {
        input.value = saved;
        checkPostalCode(root);
      }
    });
  }

  /* ---------------------------------------------------------------
     Cross-sell del carrito: alta rápida sin salir del drawer
     --------------------------------------------------------------- */
  async function quickAdd(button) {
    const variantId = button.dataset.jfXsellVariant;
    if (!variantId) return;

    const toaster = document.querySelector('ce-toaster');
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(window.Themu.routes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: ['cart-data', 'cart-drawer', 'cart-drawer-notification'],
        }),
      });

      const responseBody = await response.json();

      if (!response.ok) {
        toaster?.makeToast('warning', responseBody.description, 7000);
        return;
      }

      document.dispatchEvent(new CustomEvent('Themu:cart:add', { detail: responseBody.sections }));
    } catch (error) {
      toaster?.makeToast('danger', window.Themu?.t?.products?.buy_buttons?.error_message || 'Error', 7000);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /* ---------------------------------------------------------------
     Selector visual de jugos: contador de avance
     --------------------------------------------------------------- */
  function updatePickerCounter(picker) {
    const counter = picker.querySelector('[data-jf-vpick-counter]');
    if (!counter) return;

    const checked = picker.querySelector('[data-jf-vpick-input]:checked');
    counter.textContent = checked ? checked.dataset.jfVpickName || '' : '';
  }

  /* ---------------------------------------------------------------
     Guías descargables y enlaces externos: abrir en pestaña nueva
     para no abandonar la página de compra.
     --------------------------------------------------------------- */
  function externalizeLinks(scope) {
    const containers = scope.querySelectorAll('[data-jf-external-links]');

    containers.forEach((container) => {
      container.querySelectorAll('a[href]').forEach((link) => {
        if (link.target === '_blank') return;

        const href = link.getAttribute('href') || '';
        if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

        let isExternal = false;
        try {
          isExternal = new URL(link.href, window.location.href).hostname !== window.location.hostname;
        } catch (error) {
          isExternal = false;
        }

        const isFile = /\.(pdf|docx?|xlsx?|pptx?|zip)(\?|#|$)/i.test(href);

        if (isExternal || isFile) {
          link.target = '_blank';
          link.rel = link.rel ? `${link.rel} noopener` : 'noopener';
        }
      });
    });
  }

  /* ---------------------------------------------------------------
     Popup de oferta de bienvenida
     --------------------------------------------------------------- */
  function initWelcomeOffer() {
    const popup = document.querySelector('[data-jf-welcome]');
    if (!popup) return;

    const dismissKey = 'jf_welcome_dismissed_until';
    const subscribedKey = 'jf_welcome_subscribed';
    const frequencyDays = parseInt(popup.dataset.jfWelcomeFrequency || '14', 10);

    let opened = false;

    const open = () => {
      if (opened) return;
      opened = true;
      popup.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      popup.querySelector('[data-jf-welcome-focus]')?.focus({ preventScroll: true });
    };

    const close = (remember) => {
      popup.classList.remove('is-open');
      document.documentElement.style.overflow = '';

      if (remember) {
        const until = Date.now() + frequencyDays * 24 * 60 * 60 * 1000;
        storage.set(dismissKey, String(until));
      }
    };

    // Vuelta del formulario de Shopify: el popup se reabre mostrando el código.
    if (popup.querySelector('[data-jf-welcome-success]')) {
      storage.set(subscribedKey, '1');
      open();
    } else {
      const dismissedUntil = parseInt(storage.get(dismissKey) || '0', 10);
      const alreadySubscribed = storage.get(subscribedKey) === '1';
      const suppressed = alreadySubscribed || (dismissedUntil && Date.now() < dismissedUntil);

      if (!suppressed && !window.Shopify?.designMode) {
        const delay = parseInt(popup.dataset.jfWelcomeDelay || '8', 10) * 1000;
        const timer = window.setTimeout(open, delay);

        const onExitIntent = (event) => {
          if (event.clientY > 0) return;
          window.clearTimeout(timer);
          open();
        };

        document.addEventListener('mouseout', onExitIntent, { once: false });
      }

      if (window.Shopify?.designMode && popup.dataset.jfWelcomePreview === 'true') {
        open();
      }
    }

    popup.addEventListener('click', (event) => {
      if (event.target.closest('[data-jf-welcome-close]')) {
        close(true);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && popup.classList.contains('is-open')) close(true);
    });

    popup.querySelector('form')?.addEventListener('submit', () => {
      storage.set(subscribedKey, '1');
    });
  }

  /* ---------------------------------------------------------------
     Arranque + reenganche tras los repintados del carrito
     --------------------------------------------------------------- */
  function init(scope) {
    initPostalCodeCheckers(scope);
    externalizeLinks(scope);
  }

  document.addEventListener('click', (event) => {
    const zipSubmit = event.target.closest('[data-jf-zip-submit]');
    if (zipSubmit) {
      event.preventDefault();
      checkPostalCode(zipSubmit.closest('[data-jf-zip]'));
      return;
    }

    const xsellButton = event.target.closest('[data-jf-xsell-add]');
    if (xsellButton) {
      event.preventDefault();
      quickAdd(xsellButton);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const zipInput = event.target.closest('[data-jf-zip-input]');
    if (!zipInput) return;

    event.preventDefault();
    checkPostalCode(zipInput.closest('[data-jf-zip]'));
  });

  document.addEventListener('change', (event) => {
    const pickerInput = event.target.closest('[data-jf-vpick-input]');
    if (pickerInput) updatePickerCounter(pickerInput.closest('[data-jf-vpick]'));
  });

  const start = () => {
    init(document);
    initWelcomeOffer();
    document.querySelectorAll('[data-jf-vpick]').forEach(updatePickerCounter);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // El drawer del carrito se reemplaza por completo en cada actualización.
  document.addEventListener('Themu:cart:add', () => {
    window.setTimeout(() => init(document), 60);
  });
})();
